/**
 * meta/leaderboards.js — per-stall weekly and all-time boards (GDD 7.6).
 *
 * There is no backend, and the GDD's Meta/Steamworks integration is out of
 * scope here, so the global field is SIMULATED: nine rivals per stall per
 * board, drawn from a generator seeded by the board's identity.
 *
 *   weekly   → seed includes the ISO week ⇒ stable all week, new field Monday
 *   all-time → seed omits the week        ⇒ never changes
 *
 * That is the whole reason the rivals use `seededRng(key)` instead of the
 * global `rng()`: a board must read the same whether you open it before or
 * after playing a round, and a shared global stream cannot promise that.
 *
 * Rival scores are spread across the stall's own GDD 7.3 score range, so the
 * numbers next to your name are always plausible for the stall you are looking
 * at rather than being one league table for twelve different games.
 *
 * @module meta/leaderboards
 */

import {
  STALL_IDS, STALL_BY_ID, TOKEN_CURVES, RIVAL_NAMES, RIVAL_COUNT,
  RIVAL_SCORE_BAND, LEADERBOARD_BOARD_ROWS,
} from '../constants.js';
import { save } from '../core/save.js';
import { logCall } from '../core/testmode.js';
import { weeklySlice, weekKey, seededRng, seededShuffle } from './state.js';

/** How the player's own row is labelled. */
export const PLAYER_NAME = 'You';

/** All-time bests are records, so that field sits higher than a single week's. */
const ALLTIME_BAND = { low: 0.55, high: 1.15 };

/**
 * Round to a granularity that suits the stall's scale — Coin Pusher counts
 * coins to 100, Skee-Ball runs to 5000, and both should look hand-set.
 * @param {number} hi
 * @returns {number}
 */
function granularity(hi) {
  if (hi >= 1000) return 10;
  if (hi >= 200) return 5;
  return 1;
}

/**
 * The simulated global field for one board.
 * @param {string} stallId
 * @param {'weekly'|'alltime'} scope
 * @returns {Array<{ name: string, score: number, isPlayer: false }>}
 */
function rivalsFor(stallId, scope) {
  const curve = TOKEN_CURVES[stallId] || { scoreMin: 0, scoreMax: 1000 };
  const band = scope === 'alltime' ? ALLTIME_BAND : RIVAL_SCORE_BAND;
  const key = scope === 'alltime'
    ? `rivals:alltime:${stallId}`
    : `rivals:weekly:${stallId}:${weekKey()}`;

  const gen = seededRng(key);
  const names = seededShuffle([...RIVAL_NAMES], gen).slice(0, RIVAL_COUNT);
  const step = granularity(curve.scoreMax);

  return names.map((name) => {
    const t = band.low + (band.high - band.low) * gen();
    const raw = curve.scoreMax * t;
    const score = Math.max(curve.scoreMin, Math.round(raw / step) * step);
    return { name, score, isPlayer: false };
  });
}

/**
 * The player's own score on a board.
 * @param {string} stallId
 * @param {'weekly'|'alltime'} scope
 * @returns {number}
 */
function playerScore(stallId, scope) {
  if (scope === 'alltime') return save.data.stalls?.[stallId]?.bestScore || 0;
  return weeklySlice().best[stallId] || 0;
}

export const leaderboards = {
  /**
   * A full board: rivals plus the player, sorted and ranked.
   * Ties break in the player's favour — losing a tiebreak to a simulated goose
   * is a bad feeling for no design gain.
   *
   * @param {string} stallId
   * @param {'weekly'|'alltime'} [scope='weekly']
   * @returns {{ stallId, stallName, scope, weekKey, rows: Array<object>,
   *             playerRank: number, playerScore: number }}
   */
  board(stallId, scope = 'weekly') {
    const mine = playerScore(stallId, scope);
    const rows = [...rivalsFor(stallId, scope), { name: PLAYER_NAME, score: mine, isPlayer: true }];

    rows.sort((a, b) => (b.score - a.score) || (a.isPlayer ? -1 : b.isPlayer ? 1 : 0));
    rows.forEach((r, i) => { r.rank = i + 1; });

    return {
      stallId,
      stallName: STALL_BY_ID[stallId]?.name ?? stallId,
      scope,
      weekKey: weekKey(),
      rows,
      playerRank: rows.find((r) => r.isPlayer)?.rank ?? rows.length,
      playerScore: mine,
    };
  },

  /**
   * Record a round's score. Called by `stalls/manager.js` on every round end.
   * Signature preserved from the stub (async, resolves to a rank).
   *
   * @param {string} stallId
   * @param {number} score
   * @returns {Promise<{ rank: number|null, submitted: boolean, weeklyBest: boolean }>}
   */
  async submit(stallId, score) {
    logCall('meta', 'leaderboards.submit', [stallId, score]);
    if (!STALL_IDS.includes(stallId) || !Number.isFinite(score)) {
      return { rank: null, submitted: false, weeklyBest: false };
    }

    const weekly = weeklySlice();
    const previous = weekly.best[stallId] || 0;
    const weeklyBest = score > previous;
    if (weeklyBest) {
      weekly.best[stallId] = score;
      save.markDirty();
    }

    return { rank: this.board(stallId, 'weekly').playerRank, submitted: true, weeklyBest };
  },

  /**
   * Top N rows of a board. Signature preserved from the stub.
   * @param {string} stallId
   * @param {number} [n=10]
   * @param {'weekly'|'alltime'} [scope='weekly']
   * @returns {Promise<Array<{ rank, name, score, isPlayer }>>}
   */
  async top(stallId, n = 10, scope = 'weekly') {
    logCall('meta', 'leaderboards.top', [stallId, n]);
    return this.board(stallId, scope).rows.slice(0, n);
  },

  /**
   * Compact lines for the physical hub boards, which are small and read at
   * a distance.
   * @param {string} stallId
   * @param {'weekly'|'alltime'} scope
   * @param {number} [rows]
   * @returns {string[]} First line is the stall name.
   */
  boardLines(stallId, scope, rows = LEADERBOARD_BOARD_ROWS) {
    const b = this.board(stallId, scope);
    const format = (r) => `${r.rank}. ${r.isPlayer ? '▶ YOU' : r.name}  ${r.score}`;

    // The player's row always makes the cut. A board that shows the top six and
    // nothing else tells a new player nothing about themselves — they are rank
    // ten on every board until they have played, which is exactly when the
    // board most needs to say "here is where you are".
    const top = b.rows.slice(0, rows);
    if (!top.some((r) => r.isPlayer)) {
      top[top.length - 1] = b.rows.find((r) => r.isPlayer);
    }
    return [b.stallName.toUpperCase(), ...top.map(format)];
  },

  /** @returns {string} The ISO week the weekly boards belong to. */
  weekKey() {
    return weekKey();
  },

  /** @returns {number} The player's best this week at a stall. */
  weeklyBest(stallId) {
    return playerScore(stallId, 'weekly');
  },

  /** @returns {string[]} Stalls in hub order — the browse/rotation order. */
  stalls() {
    return [...STALL_IDS];
  },

  /**
   * Every board at once, for the DOM browse view.
   * @param {'weekly'|'alltime'} [scope='weekly']
   * @returns {Array<object>}
   */
  all(scope = 'weekly') {
    return STALL_IDS.map((id) => this.board(id, scope));
  },
};

export default leaderboards;
